import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { SettingsEntrySource } from '@/analytics/enums/settings-entry.enums'
import { InlineMessage, type InlineMessageProps } from '@/components/inline-message/InlineMessage'
import { Modal, ModalContainer, ModalFooterWithActions, ModalHeader, TabbedModal } from '@/components/modal'
import { SearchField } from '@/components/search-field/SearchField'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType } from '@/constants/constants-ui'
import { kToastDurationMediumMs } from '@/constants/constants-toast'
import { BrowseMatterFiles } from '@/user_files/components/BrowseMatterFiles'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { selectRootFolderIdForMatter } from '@/user_files/folders/store/selectors'
import { kMaxSearchQueryLength } from '@/user_files/search/constants'
import { usePickerFileSearch } from '@/user_files/search/hooks/usePickerFileSearch'
import { useBrowseFiles } from '@/user_files/components/useBrowseFiles'
import { useRecursiveFolderSelection } from '@/user_files/components/useRecursiveFolderSelection'
import { kGetFeatureFlagStatus, kFeatureFlags } from '@/constants/constants-feature-flags'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { DmsProviderTabIcon } from '@/user_files/external-storage/components/DmsProviderTabIcon'
import { FilePickerConnectivityBanner } from '@/user_files/external-storage/components/FilePickerConnectivityBanner'
import { useExternalFilesTab } from '@/user_files/external-storage/hooks/useExternalFilesTab'
import { useMatterSyncRecovery } from '@/user_files/external-storage/hooks/useMatterSyncRecovery'
import { isActiveExternalStorageItem, isVisibleExternalStorageItem } from '@/user_files/external-storage/utils/external-storage-items'
import { isDetectedExtensionAcceptedForUserFilesUpload } from '@/user_files/utils/file-type-utils'
import { isSelectableProcessingStatus, isWaitingForOcrQuota } from '@/user_files/utils/processing-status'
import { selectFilesById } from '@/user_files/files/store/selectors'
import { uniquePreserveOrder } from '@/util/array-utils'
import { getEmptyFolderOutcomeMessage } from '@/user_files/utils/empty-folder-outcome-message'
import { groupExtractedFiles } from '@/user_files/utils/group-extracted-files'

export const ADD_FILES_MODAL_TAB = {
  MATTER_FILES: 'matter-files',
  EXTERNAL_FILES: 'external-files',
} as const

const ADD_FILES_TAB_MATTER_FILES = ADD_FILES_MODAL_TAB.MATTER_FILES
const ADD_FILES_TAB_TEMPLATES = 'templates'
const ADD_FILES_TAB_EXTERNAL_FILES = ADD_FILES_MODAL_TAB.EXTERNAL_FILES
const EMPTY_SELECTED_FILE_IDS: string[] = []
export type AddFilesModalInitialTab = (typeof ADD_FILES_MODAL_TAB)[keyof typeof ADD_FILES_MODAL_TAB]

export type AddFilesModalProps = {
  /** Whether the modal is open. */
  open: boolean
  /** Optional upload controls supplied by the caller. */
  uploadPanel?: ReactNode
  /** Called when the modal is closed. */
  onClose: () => void
  /** Matter whose files are listed (conversation matter from the parent; not switchable inside this modal). */
  matterId: string
  /**
   * Called with the selected file ids when the user confirms ("Done"). The caller owns what happens next, and returns
   * `{ showedToast: true }` when it showed its own toast so the modal doesn't show one over it.
   */
  onFilesSelected: (userFileIds: string[]) => { showedToast: boolean } | void
  validateSelection?: (ids: string[]) => string | undefined
  submitSelection?: (ids: string[]) => Promise<boolean>
  /** Optional file ids to preselect when the modal opens. */
  initialSelectedFileIds?: string[]
  /** 'single' replaces the prior pick on each toggle; 'multiple' adds/removes. Defaults to 'multiple'. */
  selectionMode?: 'single' | 'multiple'
  /** Modal title. Defaults to 'Add files'. */
  title?: string
  /** Optional. Folder to open the browse UI on; otherwise browsing starts at the matter root. If set, must belong to `matterId`; invalid ids fall back to root. */
  initialFolderId?: string
  /** Optional status message above the footer actions. */
  inlineMessage?: InlineMessageProps
  /** Optional file-level selection rule. Defaults to allowing every loaded file. */
  isFileSelectable?: (file: UserFileRead) => boolean
  /** Include uploaded files still processing. Defaults to false; failed and pending uploads stay excluded. */
  includeProcessingFiles?: boolean
  /** Optional tooltip shown for files rejected by `isFileSelectable`. */
  fileNotSelectableMessage?: string | ((file: UserFileRead) => string | undefined)
  /** Optional tab to show first when the modal opens. */
  initialTab?: AddFilesModalInitialTab
}

export function AddFilesModal({
  open,
  uploadPanel,
  onClose,
  matterId,
  onFilesSelected,
  validateSelection,
  submitSelection,
  initialSelectedFileIds = EMPTY_SELECTED_FILE_IDS,
  selectionMode = 'multiple',
  title = 'Add files',
  initialFolderId,
  inlineMessage,
  isFileSelectable,
  includeProcessingFiles = false,
  fileNotSelectableMessage,
  initialTab = ADD_FILES_TAB_MATTER_FILES,
}: AddFilesModalProps) {
  const dispatch = useAppDispatch()
  const matterRootFolderId = useAppSelector((state) => selectRootFolderIdForMatter(state, { matterId }))
  const singleSelect = selectionMode === 'single'
  const search = usePickerFileSearch({ matterId })
  const { clearSearch } = search

  const [applying, setApplying] = useState(false)
  const filesById = useAppSelector(selectFilesById)
  const [selectedFileIdsState, setSelectedFileIds] = useState<string[]>(initialSelectedFileIds)
  const selectedFileIds = useMemo(() => selectedFileIdsState.filter((id) => !isWaitingForOcrQuota(filesById[id])), [selectedFileIdsState, filesById])
  const [activeTabId, setActiveTabId] = useState<string>(ADD_FILES_TAB_MATTER_FILES)
  // Folder a synced search result aims at; consumed as the provider browse's initial folder.
  const [externalFolderTarget, setExternalFolderTarget] = useState<string | null>(null)
  // Anchor for Shift-click range selection within the current visible file list.
  const lastSelectedFileIdRef = useRef<string | null>(null)

  const matterFilesBrowse = useBrowseFiles({
    matterId,
    open,
    initialFolderId,
  })

  const externalFilesTab = useExternalFilesTab({
    matterId,
    open,
    active: activeTabId === ADD_FILES_TAB_EXTERNAL_FILES,
    fallbackBreadcrumbItems: matterFilesBrowse.breadcrumbItems,
    initialFolderIdOverride: externalFolderTarget,
  })
  const activeExternalMapping = externalFilesTab.activeExternalMapping
  const syncRecovery = useMatterSyncRecovery({
    matterId,
    connectedByUserId: activeExternalMapping?.connected_by_user_id,
    connectorFallbackLabel: activeExternalMapping?.account_display_email,
    settingsEntrySource: SettingsEntrySource.FilePickerConnectDms,
  })

  // Picking a tab by hand always means "show me this source from the top", so any folder a search
  // result had aimed at is forgotten.
  const handleTabChange = (tabId: string) => {
    setExternalFolderTarget(null)
    setActiveTabId(tabId)
  }

  const showTabbedLayout = externalFilesTab.showExternalTab
  const isExternalFileFormatSupported = useCallback((file: UserFileRead) => isDetectedExtensionAcceptedForUserFilesUpload(file.detected_extension), [])
  const isExternalFileSelectable = useCallback(
    (file: UserFileRead) => isExternalFileFormatSupported(file) && (isFileSelectable?.(file) ?? true),
    [isExternalFileFormatSupported, isFileSelectable]
  )
  // Results span both storage sources, so the tab no longer says which rules a row plays by — each
  // row does. Only an actively synced row follows the provider tab's rules; local and tombstoned
  // rows keep the caller's. Getting this wrong would offer a .docx result in the PDF-only pickers.
  const isSearchResultSelectable = useCallback(
    (file: UserFileRead) => (isActiveExternalStorageItem(file) ? isExternalFileSelectable(file) : isFileSelectable?.(file) ?? true),
    [isFileSelectable, isExternalFileSelectable]
  )
  const isSearchResultFormatSupported = useCallback(
    (file: UserFileRead) => (isActiveExternalStorageItem(file) ? isExternalFileFormatSupported(file) : true),
    [isExternalFileFormatSupported]
  )
  const activeFileSelectionRule = search.isActive
    ? isSearchResultSelectable
    : activeTabId === ADD_FILES_TAB_EXTERNAL_FILES
    ? isExternalFileSelectable
    : isFileSelectable
  // Matter tab shows local plus tombstoned (sync-disconnected) files; actively synced files stay
  // on the provider tab. Search spans both, so it takes the union of the two tabs' rules.
  const filterRecursiveSelectableFiles = useCallback(
    (file: UserFileRead) => {
      if (isWaitingForOcrQuota(file)) return false
      if (search.isActive) return !isActiveExternalStorageItem(file) || isVisibleExternalStorageItem(file, externalFilesTab.syncedProvider)
      return activeTabId === ADD_FILES_TAB_EXTERNAL_FILES
        ? isVisibleExternalStorageItem(file, externalFilesTab.syncedProvider)
        : !isActiveExternalStorageItem(file)
    },
    [search.isActive, activeTabId, externalFilesTab.syncedProvider]
  )

  const {
    clearRecursiveFolderSelection,
    emptySelectedFolderCount,
    getFolderSelectionState,
    getFolderSelectionTooltipMessage,
    hasRecursiveFolderSelectionPending,
    recursiveFolderSelectionMessage,
    toggleFolderSelection,
  } = useRecursiveFolderSelection({
    dispatch,
    filterSelectableFiles: filterRecursiveSelectableFiles,
    isFileSelectable: activeFileSelectionRule,
    includeProcessingFiles,
    selectedFileIds,
    setSelectedFileIds,
  })

  // Reset selection and tab when the modal is opened.
  useEffect(() => {
    if (!open) return
    setSelectedFileIds(initialSelectedFileIds)
    setActiveTabId(initialTab)
    lastSelectedFileIdRef.current = null
    clearRecursiveFolderSelection()
    clearSearch()
    setExternalFolderTarget(null)
  }, [open, matterId, initialSelectedFileIds, initialTab, clearRecursiveFolderSelection, clearSearch])

  useEffect(() => {
    if (activeTabId === ADD_FILES_TAB_EXTERNAL_FILES && !externalFilesTab.externalSyncStatus.loading && !showTabbedLayout) {
      setActiveTabId(ADD_FILES_TAB_MATTER_FILES)
    }
  }, [activeTabId, externalFilesTab.externalSyncStatus.loading, showTabbedLayout])

  const matterTabFolders = useMemo(() => matterFilesBrowse.folders.filter((folder) => !isActiveExternalStorageItem(folder)), [matterFilesBrowse.folders])
  const matterTabFiles = useMemo(
    () => matterFilesBrowse.files.filter((file) => !isActiveExternalStorageItem(file) && !isWaitingForOcrQuota(file)),
    [matterFilesBrowse.files]
  )
  const searchFiles = useMemo(() => search.files.filter((file) => !isWaitingForOcrQuota(file)), [search.files])
  const externalFiles = useMemo(() => externalFilesTab.browseProps.files.filter((file) => !isWaitingForOcrQuota(file)), [externalFilesTab.browseProps.files])
  // Synced files are `user_files` rows in this matter, so the matter-scoped endpoints return them —
  // and that is intended: search is the "find anything in this matter" affordance, so results keep
  // the actively synced rows the matter tab leaves to the provider tab.
  const searchFolders = showTabbedLayout ? search.folders : search.folders.filter((folder) => !isActiveExternalStorageItem(folder))
  const panelFolders = search.isActive ? searchFolders : matterTabFolders
  const panelFiles = search.isActive ? searchFiles : matterTabFiles
  // Grouped order for shift-click ranges, including collapsed extracted attachments the list is
  // not painting. Matching Matter Files select-all: hidden children stay selectable so a range
  // across an email can include its attachments without expanding first.
  const visibleFiles = useMemo(() => {
    if (search.isActive) return searchFiles
    const browseFiles = activeTabId === ADD_FILES_TAB_EXTERNAL_FILES ? externalFiles : matterTabFiles
    return groupExtractedFiles(browseFiles).map(({ file }) => file)
  }, [search.isActive, searchFiles, activeTabId, externalFiles, matterTabFiles])

  const isFileAllowedForSelection = (file: UserFileRead) =>
    !isWaitingForOcrQuota(file) && isSelectableProcessingStatus(file.processing_status, includeProcessingFiles) && (activeFileSelectionRule?.(file) ?? true)

  const handleToggleFile = (file: UserFileRead, options?: { shiftKey?: boolean }) => {
    const fileId = file.id
    const canSelectFile = isFileAllowedForSelection(file)
    const fileWasSelected = selectedFileIds.includes(fileId)
    const anchorFileId = lastSelectedFileIdRef.current
    const anchorIndex = anchorFileId ? visibleFiles.findIndex((visibleFile) => visibleFile.id === anchorFileId) : -1
    const fileIndex = visibleFiles.findIndex((visibleFile) => visibleFile.id === fileId)

    // Shift-click only adds the range; it does not remove existing selections. The range uses
    // grouped order above, so collapsed attachments between the anchor and click are included.
    if (!singleSelect && options?.shiftKey && canSelectFile && anchorIndex >= 0 && fileIndex >= 0 && anchorIndex !== fileIndex) {
      const startIndex = Math.min(anchorIndex, fileIndex)
      const endIndex = Math.max(anchorIndex, fileIndex)
      const rangeFileIds = visibleFiles
        .slice(startIndex, endIndex + 1)
        .filter(isFileAllowedForSelection)
        .map((visibleFile) => visibleFile.id)

      setSelectedFileIds((prev) => uniquePreserveOrder([...prev, ...rangeFileIds]))
      lastSelectedFileIdRef.current = fileId
      return
    }

    setSelectedFileIds((prev) => {
      const isSelected = prev.includes(fileId)
      if (!isSelected && !canSelectFile) return prev
      // Single-select replaces the prior choice on each toggle; multi-select adds/removes.
      if (singleSelect) return isSelected ? [] : [fileId]
      return isSelected ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    })

    if (!fileWasSelected && canSelectFile) {
      lastSelectedFileIdRef.current = fileId
    }
  }

  const handleClose = () => {
    clearRecursiveFolderSelection()
    onClose()
  }

  const selectionError = validateSelection?.(selectedFileIds)
  const handleApply = async () => {
    if (selectionError || applying) return
    let selectionOutcome: { showedToast: boolean } | void = undefined
    if (submitSelection) {
      setApplying(true)
      try {
        if (!(await submitSelection(selectedFileIds))) return
      } finally {
        setApplying(false)
      }
    } else selectionOutcome = onFilesSelected(selectedFileIds)
    handleClose()

    // Ticking an empty folder adds nothing, which is otherwise invisible: the modal just closes. The
    // toast comes after the close because Radix aria-hides everything outside an open dialog, which
    // would leave it unannounced and its close button unreachable.
    if (emptySelectedFolderCount === 0 || selectionOutcome?.showedToast) return

    // The picker opens with the caller's already-attached files ticked, so the count has to be what
    // this interaction added — the size of the whole selection would report attachments as additions.
    const initiallySelectedFileIdSet = new Set(initialSelectedFileIds)
    const addedFileCount = selectedFileIds.filter((id) => !initiallySelectedFileIdSet.has(id)).length

    dispatch(
      openGlobalToast({
        type: GlobalToastType.INFO,
        message: getEmptyFolderOutcomeMessage(addedFileCount, emptySelectedFolderCount),
        durationMs: kToastDurationMediumMs,
      })
    )
  }

  const handleClearSelection = () => {
    setSelectedFileIds([])
    lastSelectedFileIdRef.current = null
    clearRecursiveFolderSelection()
  }

  const footerInlineMessage: InlineMessageProps | undefined = recursiveFolderSelectionMessage
    ? {
        variant: recursiveFolderSelectionMessage.variant,
        children: recursiveFolderSelectionMessage.text,
      }
    : inlineMessage
  const isSelectionTab = !showTabbedLayout || activeTabId === ADD_FILES_TAB_MATTER_FILES || activeTabId === ADD_FILES_TAB_EXTERNAL_FILES
  const footer = (
    <ModalFooterWithActions
      inlineMessage={selectionError ? { variant: 'error', children: selectionError } : footerInlineMessage}
      tertiaryText={`${selectedFileIds.length} ${selectedFileIds.length === 1 ? 'file' : 'files'} selected`}
      secondaryAction={{
        label: 'Clear',
        onClick: handleClearSelection,
        disabled: !isSelectionTab,
      }}
      primaryAction={{
        label: 'Done',
        onClick: handleApply,
        // Done applies the current selection, including an empty one — so deselecting (or Clear)
        // and confirming clears the attachment instead of trapping the user. Disabled only on the
        // non-files tabs, or while a recursive folder selection is still resolving.
        disabled: !isSelectionTab || hasRecursiveFolderSelectionPending || Boolean(selectionError) || applying,
      }}
    />
  )
  /**
   * Opens a synced folder on the provider tab. Search spans both sources, so a synced folder is
   * findable from the matter tab — where its contents are filtered out, making it a dead end
   * without this. Which mechanism works depends on where we are: arriving from the matter tab the
   * provider browse resets as its tab opens, so the target has to be its initial folder; already
   * on the provider tab there is no such reset, so it is an ordinary navigation.
   */
  const openSyncedFolder = (folderId: string) => {
    setExternalFolderTarget(folderId)

    if (activeTabId === ADD_FILES_TAB_EXTERNAL_FILES) {
      externalFilesTab.browseProps.onNavigateToFolder(folderId)
      return
    }

    setActiveTabId(ADD_FILES_TAB_EXTERNAL_FILES)
  }

  // Navigating into a result leaves search, so the breadcrumb the user lands on describes what they
  // are looking at. Its ancestors may never have been browsed, so pull them in for the trail.
  const handleNavigateToFolder = (folderId: string) => {
    if (!search.isActive) {
      matterFilesBrowse.onNavigateToFolder(folderId)
      return
    }

    const target = search.folders.find((folder) => folder.id === folderId)

    clearSearch()

    // Either destination builds its breadcrumb by walking parent links through the folder store, and
    // that walk stops at the first ancestor the picker never loaded. Hydrate before navigating.
    search.ancestorFolderIdsById.get(folderId)?.forEach((ancestorId) => dispatch(UserFoldersActions.fetchFolderById({ folderId: ancestorId })))

    if (target && isActiveExternalStorageItem(target)) {
      openSyncedFolder(folderId)
      return
    }

    // The destination decides the tab: a matter folder is listed by the matter panel, whichever
    // tab the search happened to be run from.
    setExternalFolderTarget(null)
    setActiveTabId(ADD_FILES_TAB_MATTER_FILES)
    matterFilesBrowse.onNavigateToFolder(folderId)
  }

  const showConnectivityBanner =
    !search.isActive && activeTabId === ADD_FILES_TAB_EXTERNAL_FILES && externalFilesTab.hasUnhealthyAccount && externalFilesTab.syncedProvider != null
  const connectivityBanner =
    showConnectivityBanner && externalFilesTab.syncedProvider ? (
      <div className="shrink-0 px-4 py-2">
        <FilePickerConnectivityBanner
          provider={externalFilesTab.syncedProvider}
          isConnector={syncRecovery.isConnector}
          connectorLabel={syncRecovery.connectorLabel}
          onReconnect={syncRecovery.onReconnect}
          onOpenMatterSettings={syncRecovery.onOpenMatterSettings}
        />
      </div>
    ) : null
  const externalSyncFailureMessage = externalFilesTab.activeExternalMapping?.failure_message
  const showExternalSyncFailure = !showConnectivityBanner && Boolean(externalSyncFailureMessage)

  const matterFilesPanel = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {uploadPanel}
      <BrowseMatterFiles
        includeProcessingFiles={includeProcessingFiles}
        breadcrumbItems={matterFilesBrowse.breadcrumbItems}
        matterRootFolderId={matterRootFolderId}
        files={panelFiles}
        folders={panelFolders}
        blockingError={search.isActive ? search.error ?? undefined : matterFilesBrowse.error}
        loading={search.isActive ? search.status === 'loading' && panelFolders.length === 0 && panelFiles.length === 0 : matterFilesBrowse.loading}
        // Folder select-all only applies to multi-select; the single-select picker (writing
        // sample) omits it so selecting a folder can't pull in more than one file.
        getFolderSelectionState={singleSelect ? undefined : getFolderSelectionState}
        getFolderSelectionTooltipMessage={singleSelect ? undefined : getFolderSelectionTooltipMessage}
        onToggleFolderSelection={singleSelect ? undefined : toggleFolderSelection}
        onNavigateToFolder={handleNavigateToFolder}
        onRetry={search.isActive ? search.retry : matterFilesBrowse.retry}
        onToggleFile={handleToggleFile}
        selectedFileIds={selectedFileIds}
        isFileSelectable={search.isActive ? isSearchResultSelectable : isFileSelectable}
        isFileFormatSupported={search.isActive ? isSearchResultFormatSupported : undefined}
        fileNotSelectableMessage={fileNotSelectableMessage}
        searchResults={search.isActive ? { query: search.query.trim(), locationPathById: search.locationPathById, onClearSearch: clearSearch } : undefined}
      />
    </div>
  )

  const searchField = (
    <SearchField aria-label="Search matter files" maxLength={kMaxSearchQueryLength} onChange={search.setQuery} placeholder="Search" value={search.query} />
  )

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose()
      }}
    >
      {showTabbedLayout ? (
        <TabbedModal
          // Figma preserves the list height and adds the 48px banner row to the standard modal.
          className={showConnectivityBanner ? 'h-[526px] bg-white' : 'bg-white'}
          title={title}
          width="lg"
          height="md"
          activeTabId={activeTabId}
          onTabChange={handleTabChange}
          titleTrailing={searchField}
          hideTabBar={search.isActive}
          footer={footer}
          tabs={[
            {
              id: ADD_FILES_TAB_EXTERNAL_FILES,
              label: externalFilesTab.syncedProviderLabel ?? '',
              leadingIcon: externalFilesTab.syncedProvider ? (
                <DmsProviderTabIcon provider={externalFilesTab.syncedProvider} unhealthy={externalFilesTab.hasUnhealthyAccount} />
              ) : undefined,
              ariaLabel: externalFilesTab.hasUnhealthyAccount ? `${externalFilesTab.syncedProviderLabel}, disconnected` : undefined,
              // Results span both sources, so the strip hides and whichever tab is selected shows
              // them. Leaving the selection alone is what keeps the provider browse mounted at the
              // folder the user was in, ready for when the query clears.
              panel: search.isActive ? (
                matterFilesPanel
              ) : (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  {connectivityBanner || showExternalSyncFailure ? (
                    <div className="shrink-0 border-b border-brand-neutral-200">
                      {connectivityBanner}
                      {showExternalSyncFailure && (
                        <div className="px-6 py-4">
                          <InlineMessage variant="warning">
                            Sync paused. {externalSyncFailureMessage} Existing synced files are still available, and we’ll retry automatically.
                          </InlineMessage>
                        </div>
                      )}
                    </div>
                  ) : null}
                  <BrowseMatterFiles
                    includeProcessingFiles={includeProcessingFiles}
                    {...externalFilesTab.browseProps}
                    files={externalFiles}
                    getFolderSelectionState={singleSelect ? undefined : getFolderSelectionState}
                    getFolderSelectionTooltipMessage={singleSelect ? undefined : getFolderSelectionTooltipMessage}
                    onToggleFolderSelection={singleSelect ? undefined : toggleFolderSelection}
                    onToggleFile={handleToggleFile}
                    selectedFileIds={selectedFileIds}
                    isFileSelectable={isFileSelectable}
                    isFileFormatSupported={isExternalFileFormatSupported}
                    fileNotSelectableMessage={fileNotSelectableMessage}
                  />
                </div>
              ),
            },
            {
              id: ADD_FILES_TAB_MATTER_FILES,
              label: 'Matter Files',
              panel: matterFilesPanel,
            },
            ...(kGetFeatureFlagStatus(kFeatureFlags.TEMPLATES_TAB)
              ? [
                  {
                    id: ADD_FILES_TAB_TEMPLATES,
                    label: 'Templates',
                    panel: search.isActive ? (
                      matterFilesPanel
                    ) : (
                      <div className="flex min-h-[240px] flex-col items-center justify-center px-6 py-10 text-center text-sm text-brand-neutral-500">
                        <p>Templates are not available yet.</p>
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
        />
      ) : (
        <ModalContainer className="bg-white" width="lg" height="md" data-ds-component="AddFilesModal">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col pt-6">
            <div className="shrink-0 px-6 pb-4">
              <ModalHeader title={title} titleTrailing={searchField} />
            </div>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-y border-brand-neutral-200">{matterFilesPanel}</div>
            <div className="mt-auto shrink-0 px-6 py-4">{footer}</div>
          </div>
        </ModalContainer>
      )}
    </Modal>
  )
}
